import { LANDING_HEADER_HEIGHT } from '@/components/landing/landing-header.component';
import { Devices, Insights, RecordVoiceOver } from '@mui/icons-material';
import { Box, Chip, Container, Grid, Stack, Typography } from '@mui/material';
import { tDynamic } from '@/lib/i18n';
import { brandVars as brand } from '@/theme/theme';
import type { FunctionComponent, ElementType } from 'react';
import { useTranslation } from 'react-i18next';

type Feature = {
  titleKey: string;
  descriptionKey: string;
  icon: ElementType;
  iconBg: string;
  badgeKey?: string;
};

const features: Feature[] = [
  {
    titleKey: 'landing:features.items.communication.title',
    descriptionKey: 'landing:features.items.communication.description',
    icon: Devices,
    iconBg: brand.colors.blue100
  },
  {
    titleKey: 'landing:features.items.plainEnglish.title',
    descriptionKey: 'landing:features.items.plainEnglish.description',
    icon: RecordVoiceOver,
    iconBg: brand.colors.blue50
  },
  {
    titleKey: 'landing:features.items.learning.title',
    descriptionKey: 'landing:features.items.learning.description',
    icon: Insights,
    iconBg: brand.colors.purple50,
    badgeKey: 'landing:features.items.learning.badge'
  }
];

export const LandingFeatures: FunctionComponent = () => {
  const { t } = useTranslation();

  return (
    <Box
      id="features"
      component="section"
      sx={(theme) => ({
        // Offset anchor scrolling by the fixed header height so the heading
        // isn't hidden underneath. scrollMarginTop is not a spacing-aware sx
        // key, so resolve LANDING_HEADER_HEIGHT (spacing units) to px here.
        scrollMarginTop: {
          xs: theme.spacing(LANDING_HEADER_HEIGHT.xs),
          sm: theme.spacing(LANDING_HEADER_HEIGHT.sm)
        },
        py: { xs: 8, sm: 10, lg: 12 },
        px: { xs: 2, sm: 3 },
        bgcolor: 'background.subtle'
      })}
    >
      <Container maxWidth="lg">
        <Stack sx={{ alignItems: 'center', mb: { xs: 6, sm: 8 } }}>
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '1.75rem', sm: '2rem', lg: '2.25rem' },
              textAlign: 'center',
              mb: 2
            }}
          >
            {t('landing:features.title')}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontSize: { xs: '1rem', sm: '1.125rem' },
              color: 'text.secondary',
              textAlign: 'center',
              maxWidth: 600
            }}
          >
            {t('landing:features.subtitle')}
          </Typography>
        </Stack>

        <Stack sx={{ gap: { xs: 8, lg: 10 } }}>
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <Grid
                key={feature.titleKey}
                container
                spacing={{ xs: 4, lg: 8 }}
                sx={{ alignItems: 'center' }}
              >
                <Grid
                  size={{ xs: 12, lg: 6 }}
                  sx={{ order: { xs: 1, lg: index % 2 === 0 ? 1 : 2 } }}
                >
                  <Stack sx={{ gap: 2 }}>
                    {feature.badgeKey && (
                      <Box>
                        <Chip
                          label={tDynamic(t, feature.badgeKey)}
                          size="small"
                          sx={{
                            bgcolor: brand.colors.purple50,
                            color: 'secondary.main',
                            fontWeight: 500,
                            fontSize: '0.75rem'
                          }}
                        />
                      </Box>
                    )}
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: {
                          xs: '1.375rem',
                          sm: '1.625rem',
                          lg: '1.75rem'
                        },
                        fontWeight: 600,
                        lineHeight: 1.3
                      }}
                    >
                      {tDynamic(t, feature.titleKey)}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: 'text.secondary',
                        fontSize: { xs: '1rem', sm: '1.125rem' },
                        lineHeight: 1.7
                      }}
                    >
                      {tDynamic(t, feature.descriptionKey)}
                    </Typography>
                  </Stack>
                </Grid>

                <Grid
                  size={{ xs: 12, lg: 6 }}
                  sx={{ order: { xs: 2, lg: index % 2 === 0 ? 2 : 1 } }}
                >
                  <Box
                    sx={(theme) => ({
                      // Light: per-feature brand tint. Dark: collapse to the
                      // shared `brand.infoBg` so all illustration cards share
                      // one translucent blue overlay (the per-feature pastels
                      // would lose contrast on the dark page background).
                      bgcolor: feature.iconBg,
                      ...theme.applyStyles('dark', {
                        bgcolor: 'var(--mui-palette-brand-infoBg)'
                      }),
                      borderRadius: 4,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: { xs: 200, sm: 260, lg: 300 },
                      border: '1px solid',
                      borderColor: 'divider'
                    })}
                  >
                    <Icon
                      sx={{
                        fontSize: { xs: 80, sm: 100, lg: 120 },
                        color: 'primary.main',
                        opacity: 0.8
                      }}
                    />
                  </Box>
                </Grid>
              </Grid>
            );
          })}
        </Stack>
      </Container>
    </Box>
  );
};
